import * as React from 'react';
import { NS } from '../../../common/namespaces';
import SvgEditService from '../../../services/svg-edit-service';
import { SvgAnnotations } from '../../svg-annotations';
import { isPDComponentElement } from '../../../common/pdUtils';
import { InputUnits } from '../../input-units';

enum ECompRepeatDir {
    Down,
    Right,
    Up,
    Left
}

interface IRepeatOptionsState {
    repeatDirection: ECompRepeatDir;
    maxRepeats: number;
    columnGap: number | string;
    rowGap: number | string;
    docMarginRight: number | string;
    docMarginBottom: number | string;
}

const GetDefaultState = (): IRepeatOptionsState => {
    let s: IRepeatOptionsState = {
        repeatDirection: ECompRepeatDir.Down,
        maxRepeats: 0,
        columnGap: 0.5,
        rowGap: 0.5,
        docMarginRight: 0.0,
        docMarginBottom: 0.0,
    };

    return s;
};


interface IAttrMap {
    [key: string]: { attr: string, format?: (v: any) => string }
}

export class PDTagEditor_ComponentRepeatOptions extends React.Component<{}, IRepeatOptionsState> {
    state = GetDefaultState();

    private svgEditService = SvgEditService.Get(this, () => this.onSelectionChanged());

    private stateKeyToAttrMap: IAttrMap = {
        "maxRepeats": { attr: "zaa:component-repeat-limit", format: (v) => Math.abs(Math.round(Number(v.toString()))).toString() },
        "columnGap": { attr: "zaa:component-repeat-column-gap" },
        "rowGap": { attr: "zaa:component-repeat-row-gap" },
        "docMarginRight": { attr: "zaa:element-document-margin-right" },
        "docMarginBottom": { attr: "zaa:element-document-margin-bottom" },
    }

    public componentDidMount() {
        this.onSelectionChanged(); // Trigger on first load
    }

    private onSelectionChanged = () => {
        const elements = this.svgEditService.selectedElements;

        if (elements.length != 1 || !elements[0]) {
            this.setState({});
            return;
        }

        this.setState(PDTagEditor_ComponentRepeatOptions.extractStateFrom(elements[0]), () => {
            InputUnits.UpdateAllInputs();
            SvgAnnotations.UpdateAnnotations();
        });
    }

    public static readonly extractStateFrom = (el: SVGElement) => {
        let newState = GetDefaultState();

        const repeatColsFirst = el.getAttribute("zaa:component-repeat-columns-first")?.toLowerCase() === "true";
        const repeatInvertDir = el.getAttribute("zaa:component-repeat-invert-direction")?.toLowerCase()=== "true";

        if (repeatColsFirst) {
            newState.repeatDirection = repeatInvertDir ? ECompRepeatDir.Left : ECompRepeatDir.Right;
        } else {
            newState.repeatDirection = repeatInvertDir ? ECompRepeatDir.Up : ECompRepeatDir.Down;
        }

        newState.maxRepeats = parseInt(el.getAttribute("zaa:component-repeat-limit") ?? newState.maxRepeats.toString());
        newState.columnGap = el.getAttribute("zaa:component-repeat-column-gap") ?? newState.columnGap;
        newState.rowGap = el.getAttribute("zaa:component-repeat-row-gap") ?? newState.rowGap;
        newState.docMarginRight = el.getAttribute("zaa:element-document-margin-right") ?? newState.docMarginRight;
        newState.docMarginBottom = el.getAttribute("zaa:element-document-margin-bottom") ?? newState.docMarginBottom;

        return newState;
    }

    private onChange = (key: string, value: any) => {
        this.setState(prevState => {
            let newState: IRepeatOptionsState = { ...prevState, [key]: value };

            if (key === "repeatDirection") {
                switch (+value) {
                    case ECompRepeatDir.Left:
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-columns-first", "true", null);
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-invert-direction", "true", null);
                        break;

                    case ECompRepeatDir.Up:
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-columns-first", "false", null);
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-invert-direction", "true", null);
                        break;

                    case ECompRepeatDir.Right:
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-columns-first", "true", null);
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-invert-direction", "false", null);
                        break;

                    case ECompRepeatDir.Down:
                    default:
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-columns-first", "false", null);
                        this.svgEditService.canvas.changeSelectedAttribute("zaa:component-repeat-invert-direction", "false", null);
                        break;
                }
            } else {
                const attrMap = this.stateKeyToAttrMap[key];
                this.svgEditService.canvas.changeSelectedAttribute(attrMap.attr, attrMap.format ? attrMap.format(value) : value, null);
            }
     
            return newState;
        }, () => SvgAnnotations.UpdateAnnotations());
    }

    public render() {
        const getID = (p: string) => `pd-tagEditor__${p}`;

        return (
            <div>
                <h1>Component Repeat Settings</h1>

                <table>
                    <tbody>
                        <tr>
                            <th>
                                Repeat Direction:
                            </th>
                            <td>
                                <div className="pdRadioButtonGroup">
                                    <input type="radio" id={getID("repeatDirection_down")} name="repeatDirection" value={ECompRepeatDir.Down} checked={this.state.repeatDirection == ECompRepeatDir.Down} onChange={e => this.onChange("repeatDirection", e.target.value)} />
                                    <label className="pdRadioButton" htmlFor={getID("repeatDirection_down")}>
                                        Down
                                    </label>

                                    <input type="radio" id={getID("repeatDirection_right")} name="textArepeatDirectionlignment" value={ECompRepeatDir.Right} checked={this.state.repeatDirection == ECompRepeatDir.Right} onChange={e => this.onChange("repeatDirection", e.target.value)} />
                                    <label className="pdRadioButton" htmlFor={getID("repeatDirection_right")}>
                                        Right
                                    </label>

                                    <input type="radio" id={getID("repeatDirection_up")} name="repeatDirection" value={ECompRepeatDir.Up} checked={this.state.repeatDirection == ECompRepeatDir.Up} onChange={e => this.onChange("repeatDirection", e.target.value)} />
                                    <label className="pdRadioButton" htmlFor={getID("repeatDirection_up")}>
                                        Up
                                    </label>

                                    <input type="radio" id={getID("repeatDirection_left")} name="repeatDirection" value={ECompRepeatDir.Left} checked={this.state.repeatDirection == ECompRepeatDir.Left} onChange={e => this.onChange("repeatDirection", e.target.value)} />
                                    <label className="pdRadioButton" htmlFor={getID("repeatDirection_left")}>
                                        Left
                                    </label>
                                </div>
                            </td>
                        </tr>

                        <tr>
                            <td>
                                <label htmlFor={getID("repeatWrapping")}>
                                    Max Repeats Before Wrapping:
                                </label>
                            </td>
                            <td>
                                <input id={getID("repeatWrapping")} className="form-control" type="number" step="1" value={this.state.maxRepeats} onChange={e => this.onChange("maxRepeats", e.target.value)} onBlur={() => this.onSelectionChanged()} />
                            </td>
                        </tr>

                        <tr>
                            <td>
                                <label htmlFor={getID("repeatColumnGap")}>
                                    Gap Between columns:
                                </label>
                            </td>
                            <td>
                                <InputUnits id={getID("repeatColumnGap")} min={0} max={65536} step={0.01} decimalPlaces={2} value={this.state.columnGap} onChange={e => this.onChange("columnGap", e)} />
                            </td>
                        </tr>

                        <tr>
                            <td>
                                <label htmlFor={getID("repeatRowGap")}>
                                    Gap Between Rows:
                                </label>
                            </td>
                            <td>
                                <InputUnits id={getID("repeatRowGap")} min={0} max={65536} step={0.01} decimalPlaces={2} value={this.state.rowGap} onChange={e => this.onChange("rowGap", e)} />
                            </td>
                        </tr>

                        <tr title="If set, should the repeated components enter the marked margin region, the panel itself will expand in that direction to avoid components being in that area. This allows a single expandable region of the panel.">
                            <td>
                                <label htmlFor={getID("repeatDocMarginRight")}>
                                    Document Margin Right:
                                </label>
                            </td>
                            <td>
                                <InputUnits id={getID("repeatDocMarginRight")} min={0} max={65536} step={0.01} decimalPlaces={2} value={this.state.docMarginRight} onChange={e => this.onChange("docMarginRight", e)} />
                            </td>
                        </tr>

                        <tr title="If set, should the repeated components enter the marked margin region, the panel itself will expand in that direction to avoid components being in that area. This allows a single expandable region of the panel.">
                            <td>
                                <label htmlFor={getID("repeatDocMarginBottom")}>
                                    Document Margin Bottom:
                                </label>
                            </td>
                            <td>
                                <InputUnits id={getID("repeatDocMarginBottom")} min={0} max={65536} step={0.01} decimalPlaces={2} value={this.state.docMarginBottom} onChange={e => this.onChange("docMarginBottom", e)} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }
}

SvgAnnotations.RegisterAnnotationCallback("COMPONENTREPEAT", ({ svgEditService, bbox, element, svgAnnotations, textAnnotations, defs }) => {
    if (!isPDComponentElement(element)) {
        return;
    }

    const elState = PDTagEditor_ComponentRepeatOptions.extractStateFrom(element);
    const isTagged = element.hasAttribute("zaa:component-tag");
    const zoomLevel = svgEditService.canvas.getCurrentZoom();

    const invDir = elState.repeatDirection === ECompRepeatDir.Up || elState.repeatDirection === ECompRepeatDir.Left ? -1 : 1;
    const repeatColsFirst = elState.repeatDirection === ECompRepeatDir.Right || elState.repeatDirection === ECompRepeatDir.Left;
    const repLim = elState.maxRepeats > 0 ? elState.maxRepeats : 9999999;

    if (isTagged) {
        if (elState.maxRepeats > 0) {
            textAnnotations.push(`Component Wrapping: Repeats ${elState.maxRepeats} times before wrapping`);
        } else {
            textAnnotations.push(`Component Wrapping: Doesn't wrap`);
        }
    }

    const repeatArrowColour = "rgba(0,196,64,0.6)";

    // Add SVG defs we need for arrow heads and the danger zones

    defs.push((
        <marker key="compRep_repeatArrowDef" id="comp-repeat-arrow" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={repeatArrowColour} />
        </marker>
    ));
    defs.push((
        <pattern key="compRep_hatchPatternDef" id="comp-repeat-dogmargin-hatch" width="10" height="10" patternUnits="userSpaceOnUse">
            <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 10,10" />
            <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 0,10" />
            <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 10,0" />
            <path stroke="rgba(255,0,0,0.4)" strokeLinecap="round" strokeWidth="1" d="M 5,5 L 0,0" />
        </pattern>
    ));

    const svgcontent = document.getElementById("svgcontent") as unknown as SVGSVGElement;
    const docX = svgcontent.x.baseVal.value - SvgAnnotations.OffsetX;
    const docY = svgcontent.y.baseVal.value - SvgAnnotations.OffsetY;

    // Add the margin danger zones
    const marginRight = Number(elState.docMarginRight);
    if (marginRight > 0) {
        const leftEdge = (svgEditService.canvas.contentW - marginRight) * zoomLevel;
        const bottomEdge = svgEditService.canvas.contentH * zoomLevel;

        svgAnnotations.push((
            <rect key="compRep_marginRightDangerFill" x={docX + leftEdge} y={docY} width={marginRight * zoomLevel} height={bottomEdge} fill="url(#comp-repeat-dogmargin-hatch)" pointerEvents="none" />
        ));
        svgAnnotations.push((
            <line key="compRep_marginRightDangerStroke" x1={docX + leftEdge} y1={docY} x2={docX + leftEdge} y2={docY + bottomEdge} stroke="rgba(255,0,0,0.7)" strokeWidth={2} pointerEvents="none" />
        ));
    }

    const marginBtm = Number(elState.docMarginBottom);
    if (marginBtm > 0) {
        const topEdge = (svgEditService.canvas.contentH - marginBtm) * zoomLevel;
        const rightEdge = svgEditService.canvas.contentW * zoomLevel;

        svgAnnotations.push((
            <rect key="compRep_marginBottomDangerFill" x={docX} y={docY + topEdge} width={rightEdge} height={marginBtm * zoomLevel} fill="url(#comp-repeat-dogmargin-hatch)" pointerEvents="none" />
        ));
        svgAnnotations.push((
            <line key="compRep_marginBottomDangerStroke" x1={docX} y1={docY + topEdge} x2={docX + rightEdge} y2={docY + topEdge} stroke="rgba(255,0,0,0.7)" strokeWidth={2} pointerEvents="none" />
        ));
    }

    const elColGap = Number(elState.columnGap);
    const elRowGap = Number(elState.rowGap);
    // Used to calculate the arrow path
    const calcXY = (i: number) => {
        let x, y;

        if (repeatColsFirst) {
            x = bbox.left + invDir * (bbox.width + elColGap * zoomLevel) * (i % repLim);
            y = bbox.top + invDir * (bbox.height + elRowGap * zoomLevel) * Math.floor(i / repLim);
        } else {
            x = bbox.left + invDir * (bbox.width + elColGap * zoomLevel) * Math.floor(i / repLim);
            y = bbox.top + invDir * (bbox.height + elRowGap * zoomLevel) * (i % repLim);
        }

        return { x, y };
    }

    // Generate the ghosts to show repetition and arrows to show the order.
    if (isTagged) {
        const maxGhosts = elState.maxRepeats == 0 ? 16 : elState.maxRepeats * 6;

        for (let i = 0; i < maxGhosts; ++i) {
            const { x, y } = calcXY(i);

            if (i > 0) {
                const { x: lastX, y: lastY } = calcXY(i - 1);

                svgAnnotations.push((
                    <line key={`repGhostLine-${i}`} x1={lastX + bbox.width / 2.0} y1={lastY + bbox.height / 2.0} x2={x + bbox.width / 2.0} y2={y + bbox.height / 2.0} stroke={repeatArrowColour} strokeWidth={1.5} markerEnd="url(#comp-repeat-arrow)" pointerEvents="none" />
                ));
            }

            svgAnnotations.push((
                <rect key={`repGhost-${i}`} x={x} y={y} width={bbox.width} height={bbox.height} stroke="rgba(32, 32, 204, 0.5)" strokeWidth={1} fill="none" pointerEvents="none" />
            ));
        }
    }
});
